import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { getShareContext, submitShareClaim } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, RefreshCw, ExternalLink, Clock, CheckCircle2, XCircle,
  Send, Loader2, Sparkles, AlertCircle, Clock3,
} from "lucide-react";

const PLATFORMS = [
  {
    key: "facebook",
    label: "Facebook",
    color: "#1877F2",
    supportsPrefill: false,
  },
  {
    key: "twitter",
    label: "X (Twitter)",
    color: "#000000",
    supportsPrefill: true,
    url: (text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
  {
    key: "telegram",
    label: "Telegram",
    color: "#0088CC",
    supportsPrefill: true,
    url: (text) =>
      `https://t.me/share/url?url=${encodeURIComponent(
        "https://havenpositive.online"
      )}&text=${encodeURIComponent(text)}`,
  },
];

const STATUS_CONFIG = {
  pending: {
    label: "Pending review",
    color: "text-amber-700 bg-amber-50 border-amber-200",
    icon: Clock,
  },
  approved: {
    label: "Approved",
    color: "text-emerald-700 bg-emerald-50 border-emerald-200",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Not approved",
    color: "text-red-700 bg-red-50 border-red-200",
    icon: XCircle,
  },
};

export default function EarnShare() {
  const { user, refresh } = useAuth();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [postUrl, setPostUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadContext = useCallback(async () => {
    try {
      const data = await getShareContext();
      setContext(data);
      setMessage((prev) => prev || data.template);
    } catch (err) {
      console.error("[EarnShare] load failed:", err);
      toast.error("Could not load share info");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const inviteCode = context?.invite_code || "";
  const codeIncluded = !inviteCode || message.includes(inviteCode);
  const featureEnabled = context?.enabled !== false;

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied — paste it wherever you're sharing.");
    } catch {
      toast.error("Copy failed. Select the text and copy manually.");
    }
  };

  const handleCopyLink = async () => {
    if (!context?.share_url) return;
    try {
      await navigator.clipboard.writeText(context.share_url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleResetMessage = () => {
    if (!context?.template) return;
    if (
      message !== context.template &&
      !window.confirm("Reset the message to the original template?")
    )
      return;
    setMessage(context.template);
  };

  const handlePlatformShare = (platform) => {
    if (!codeIncluded) {
      toast.error("Your message is missing the invite code. Add it back first.");
      return;
    }
    if (!platform.supportsPrefill) {
      handleCopyMessage();
      toast.info(
        "Facebook removed pre-filled text. Copy your message, then paste it into your post.",
        { duration: 6000 },
      );
      window.open("https://www.facebook.com/", "_blank", "noopener,noreferrer");
      return;
    }
    window.open(platform.url(message), "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async () => {
    const url = postUrl.trim();
    if (!url) {
      setFeedback({
        type: "error",
        text: "Please paste the URL of your post.",
      });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await submitShareClaim(url);
      setPostUrl("");
      setFeedback({ type: "success", text: res.message });
      await loadContext();
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFeedback({
        type: "error",
        text:
          typeof detail === "string"
            ? detail
            : "Submission failed. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  // ── Paused state ─────────────────────────────────────────────
  if (!loading && context && !featureEnabled) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-6">
          <div className="bg-white border border-[#E7E5E0] rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-4">
              <Clock3 className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="font-display text-2xl font-semibold mb-2">
              Share rewards are paused
            </h1>
            <p className="text-sm text-[#6B6B70] mb-6 max-w-md mx-auto leading-relaxed">
              We've temporarily paused the share-and-earn-{context.reward_tokens || 100}
              {" "}token rewards. Check back later — we'll turn it back on soon.
            </p>

            {context.claims?.length > 0 && (
              <div className="text-left bg-[#F5F3EE] border border-[#E7E5E0] rounded-xl p-4 mb-6">
                <h2 className="font-semibold text-sm mb-2">Your existing claims</h2>
                <div className="space-y-1.5">
                  {context.claims.slice(0, 5).map((c) => {
                    const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.pending;
                    const Icon = cfg.icon;
                    return (
                      <div key={c.claim_id} className="flex items-center gap-2 text-xs">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ${cfg.color}`}>
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </span>
                        <span className="text-[#6B6B70] capitalize">{c.platform}</span>
                        <span className="text-[#6B6B70] ml-auto">
                          {new Date(c.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Link
              to="/earn-tokens"
              className="neo-btn bg-purple-600 border-purple-600 inline-flex items-center gap-2"
            >
              ← Other ways to earn tokens
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Hero */}
        <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl font-semibold">
                Earn {context?.reward_tokens || 100} tokens
              </h1>
              <p className="text-sm text-[#6B6B70] mt-1">
                Share Haven on Facebook, X, or Telegram. Paste the link back.
                When an admin verifies it, {context?.reward_tokens || 100}{" "}
                tokens land in your account.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#7C3AED] mx-auto" />
          </div>
        ) : !context ? (
          <div className="text-center py-16 text-sm text-[#6B6B70]">
            Could not load share info. Please refresh.
          </div>
        ) : (
          <>
            {/* ── How it works — big, unmissable ─────────────── */}
            <section className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-4">
              <h2 className="font-display text-xl font-semibold mb-1">
                How to earn {context.reward_tokens} tokens
              </h2>
              <p className="text-sm text-[#6B6B70] mb-5">
                Follow these four steps in order. Most people take about 3 minutes.
              </p>

              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                    1
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">Copy your message</div>
                    <div className="text-xs text-[#6B6B70] mt-0.5 leading-relaxed">
                      Tap <strong className="text-[#0F0F10]">Copy message</strong>{" "}
                      in Step 1 below. The message contains your unique invite
                      code{" "}
                      <span className="font-mono font-semibold text-purple-700">
                        {inviteCode}
                      </span>{" "}
                      — that's how anyone who signs up gets linked back to you.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                    2
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">
                      Post it on Facebook, X, or Telegram
                    </div>
                    <div className="text-xs text-[#6B6B70] mt-0.5 leading-relaxed">
                      Paste the message into a real post — a group, your own
                      timeline, or a public channel. HIV, HSV, HPV, and
                      dating communities are where this works best.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                    3
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">
                      Copy the link to that specific post
                    </div>
                    <div className="text-xs text-[#6B6B70] mt-0.5 leading-relaxed">
                      Not the group's homepage. Not your profile page. The URL
                      of the actual post you just made. On each platform, look
                      for a <strong className="text-[#0F0F10]">⋯</strong> or{" "}
                      <strong className="text-[#0F0F10]">Share</strong> menu and
                      pick <strong className="text-[#0F0F10]">"Copy link"</strong>.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                    4
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">
                      Paste it below and submit
                    </div>
                    <div className="text-xs text-[#6B6B70] mt-0.5 leading-relaxed">
                      Our team reviews manually (usually within 24 hours). If
                      the post exists and contains your invite code,{" "}
                      <strong className="text-[#0F0F10]">
                        {context.reward_tokens} tokens
                      </strong>{" "}
                      land in your account.
                    </div>
                  </div>
                </li>
              </ol>

              {/* Valid vs invalid examples */}
              <div className="mt-5 p-4 rounded-lg bg-[#F5F3EE] border border-[#E7E5E0]">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#6B6B70] mb-2">
                  ✓ Valid post-link examples
                </div>
                <ul className="space-y-1 text-xs font-mono text-[#404040] break-all">
                  <li>https://www.facebook.com/groups/123456789/posts/987654321</li>
                  <li>https://x.com/yourname/status/1234567890123</li>
                  <li>https://t.me/communityname/456</li>
                </ul>
                <div className="text-xs font-semibold uppercase tracking-wide text-[#6B6B70] mt-3 mb-2">
                  ✗ These will be rejected
                </div>
                <ul className="space-y-1 text-xs font-mono text-[#404040] break-all">
                  <li>https://www.facebook.com/ <span className="font-sans text-[#9CA3AF]">← homepage</span></li>
                  <li>https://x.com/yourname <span className="font-sans text-[#9CA3AF]">← profile</span></li>
                  <li>https://www.instagram.com/p/… <span className="font-sans text-[#9CA3AF]">← wrong platform</span></li>
                </ul>
              </div>
            </section>

            {/* Step 1 */}
            <section className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold">Step 1 — Copy your message</h2>
                <button
                  onClick={handleResetMessage}
                  className="text-xs text-[#6B6B70] hover:text-[#0F0F10] flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Reset
                </button>
              </div>
              <p className="text-xs text-[#6B6B70] mb-3">
                You can edit it however you like — just make sure it still
                contains your invite code{" "}
                <span className="font-mono font-semibold text-[#7C3AED]">
                  {inviteCode}
                </span>
                .
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="neo-input h-56 resize-none font-sans text-sm leading-relaxed"
                spellCheck={false}
              />
              {!codeIncluded && (
                <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2 text-xs text-amber-900">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Your message no longer contains the invite code{" "}
                    <span className="font-mono font-semibold">{inviteCode}</span>.
                    Add it back — that's how people link back to you.
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={handleCopyMessage}
                  className="neo-btn bg-purple-600 border-purple-600 text-sm flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" /> Copy message
                </button>
                <button
                  onClick={handleCopyLink}
                  className="neo-btn neo-btn-secondary text-sm flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" /> Copy invite link
                </button>
              </div>
            </section>

            {/* Step 2 */}
            <section className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-4">
              <h2 className="font-semibold mb-1">Step 2 — Post it</h2>
              <p className="text-xs text-[#6B6B70] mb-4">
                Facebook groups, X, and Telegram all work. Just make sure the
                message (with your code) is in the post.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {PLATFORMS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => handlePlatformShare(p)}
                    className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-[#E7E5E0] hover:bg-[#F5F3EE] transition-colors"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: p.color }}
                    >
                      {p.label[0]}
                    </div>
                    <span className="text-xs font-medium">{p.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#6B6B70] mt-3 leading-relaxed">
                Facebook doesn't allow pre-filled text — copy your message
                first, then paste it into your post. Try groups like
                "HIV positive singles", "HSV dating community", or your city's
                LGBTQ+ / chronic-illness support groups.
              </p>
            </section>

            {/* Step 3 */}
            <section className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-4">
              <h2 className="font-semibold mb-1">Step 3 — Paste your post link</h2>
              <p className="text-xs text-[#6B6B70] mb-4">
                Open your post, tap the ⋯ menu, choose "Copy link", then paste
                it below.
              </p>
              <input
                type="url"
                value={postUrl}
                onChange={(e) => {
                  setPostUrl(e.target.value);
                  if (feedback) setFeedback(null);
                }}
                placeholder="https://www.facebook.com/groups/…/posts/…"
                className="neo-input"
                disabled={submitting}
              />
              {feedback && (
                <div
                  className={`mt-2 p-3 rounded-lg text-sm border ${
                    feedback.type === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                >
                  {feedback.text}
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || !postUrl.trim()}
                className="neo-btn bg-purple-600 border-purple-600 w-full mt-3 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Submit for review
                  </>
                )}
              </button>
              <p className="text-xs text-[#6B6B70] mt-2 text-center">
                Our team reviews manually — usually within 24 hours. You'll get
                a notification when it's approved.
              </p>
            </section>

            {/* Your claims */}
            {context.claims?.length > 0 && (
              <section className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-4">
                <h2 className="font-semibold mb-3">Your submissions</h2>
                <div className="space-y-2">
                  {context.claims.map((c) => {
                    const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.pending;
                    const Icon = cfg.icon;
                    return (
                      <div
                        key={c.claim_id}
                        className="border border-[#E7E5E0] rounded-xl p-3 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.color}`}
                          >
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                          <span className="text-xs text-[#6B6B70]">
                            {new Date(c.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <a
                          href={c.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-purple-600 hover:underline break-all flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          {c.post_url.length > 80
                            ? c.post_url.slice(0, 80) + "…"
                            : c.post_url}
                        </a>
                        <div className="text-xs text-[#6B6B70] capitalize">
                          {c.platform}
                          {c.status === "approved" && c.tokens_awarded > 0 && (
                            <span className="ml-2 text-emerald-700 font-semibold">
                              +{c.tokens_awarded} tokens
                            </span>
                          )}
                        </div>
                        {c.status === "rejected" && c.admin_notes && (
                          <div className="text-xs text-red-700 bg-red-50 rounded-md p-2 mt-1">
                            <strong>Reason:</strong> {c.admin_notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Guidelines */}
            <section className="bg-[#F5F3EE] border border-[#E7E5E0] rounded-2xl p-5 text-sm text-[#262626] leading-relaxed">
              <h3 className="font-semibold mb-2 text-[#0F0F10]">
                What gets approved
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  A real, public post on <strong>Facebook</strong>,{" "}
                  <strong>X (Twitter)</strong>, or <strong>Telegram</strong>{" "}
                  that contains your invite code
                </li>
                <li>Posted in a relevant community — HIV/HSV work best</li>
                <li>One post = one claim. You can share the same message many times</li>
              </ul>
              <h3 className="font-semibold mt-4 mb-2 text-[#0F0F10]">
                What gets rejected
              </h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Links to homepages, not posts</li>
                <li>Posts that don't contain your invite code</li>
                <li>Links from platforms other than Facebook, X, or Telegram</li>
                <li>The same URL submitted twice</li>
                <li>Spam or fake posts</li>
              </ul>
            </section>

            <div className="text-center mt-6">
              <Link
                to="/earn-tokens"
                className="text-sm text-[#6B6B70] hover:text-[#0F0F10]"
              >
                ← Back to other ways to earn tokens
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}